import React, { useMemo } from 'react';
import { Table, Badge } from 'react-bootstrap';
import { TimesheetEntry, ActivityCategory } from '../../api/generated/model';
import { formatHours } from '../../utils/hours';

interface TimesheetSummaryProps {
  entries: TimesheetEntry[];
}

const categoryLabels: Record<string, string> = {
  billable: 'Billable',
  non_billable: 'Non-Billable',
  pto: 'PTO',
};

const categoryVariants: Record<string, string> = {
  billable: 'success',
  non_billable: 'secondary',
  pto: 'info',
};

interface ActivitySummary {
  activityId: string;
  activityName: string;
  category: string;
  rate: number;
  hours: number;
  amount: number;
}

const TimesheetSummary: React.FC<TimesheetSummaryProps> = ({ entries }) => {
  const summary = useMemo(() => {
    // Group by activity + rate (so entries at different rates get separate rows)
    const byKey: Record<string, ActivitySummary> = {};
    let totalHours = 0;
    let totalAmount = 0;

    for (const entry of entries) {
      const actId = entry.activity_id || '';
      const hours = entry.hours || 0;
      const amount = entry.amount || 0;
      const rate = entry.hourly_rate || 0;
      const key = `${actId}:${rate}`;

      if (!byKey[key]) {
        byKey[key] = {
          activityId: actId,
          activityName: entry.activity_name || '',
          category: entry.activity_category || 'billable',
          rate,
          hours: 0,
          amount: 0,
        };
      }
      byKey[key].hours += hours;
      byKey[key].amount += amount;
      totalHours += hours;
      totalAmount += amount;
    }

    // Group activities by category
    const categories = [ActivityCategory.ActivityCategoryBillable, ActivityCategory.ActivityCategoryNonBillable, ActivityCategory.ActivityCategoryPto];
    const grouped: { category: string; activities: ActivitySummary[] }[] = [];
    for (const cat of categories) {
      const acts = Object.values(byKey).filter(a => a.category === cat);
      acts.sort((a, b) =>
        a.activityName.localeCompare(b.activityName)
        || b.rate - a.rate
        || b.hours - a.hours
      );
      if (acts.length > 0) {
        grouped.push({ category: cat, activities: acts });
      }
    }

    return { grouped, totalHours, totalAmount };
  }, [entries]);

  return (
    <Table size="sm" className="mb-0" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th className="pt-0">Activity</th>
          <th className="text-end pt-0">Rate</th>
          <th className="text-end pt-0">Hours</th>
          <th className="text-end pt-0">Amount</th>
        </tr>
      </thead>
      <tbody>
        {summary.grouped.map(group => (
          <React.Fragment key={group.category}>
            {/* Category header */}
            <tr>
              <td colSpan={4} className="pt-2 pb-1 border-0">
                <Badge bg={categoryVariants[group.category] || 'secondary'}>
                  {categoryLabels[group.category] || group.category}
                </Badge>
              </td>
            </tr>
            {/* Activity rows */}
            {group.activities.map(act => (
              <tr key={`${act.activityId}:${act.rate}`}>
                <td className="ps-3">{act.activityName}</td>
                <td className="text-end text-muted">${act.rate.toFixed(2)}/hr</td>
                <td className="text-end">{formatHours(act.hours)}</td>
                <td className="text-end">${act.amount.toFixed(2)}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
        <tr className="fw-bold border-top">
          <td>Total</td>
          <td></td>
          <td className="text-end">{formatHours(summary.totalHours)}</td>
          <td className="text-end">${summary.totalAmount.toFixed(2)}</td>
        </tr>
      </tbody>
    </Table>
  );
};

export default TimesheetSummary;
